/**
 * SOLID Review
 * S — Good. The LiveKit SDK's side of the table's video chat: create the client, connect with a token, follow the room's events into
 *     the participants and the connection state, mic and camera, and the headphone-route state that depends on them. The policy —
 *     on/off, the idle rule, the server's removal, the banner, presence — is the Application's (strict-ports FE5c), read from the view.
 * O — Good. A new room event type is a new `case` in the session's event switch.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Good. The session's state and its seven actions — the hook's shape, which the screen reads.
 * D — Good. The Application's ports (use cases, view), the request contexts, the host's LazyKit client factory, the platform's
 *     timers, clock and logger; the audio route through its own controller.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions, type IConfigReaction } from './Reactions';
import { ClientJobModel } from './ClientJobModel';
import { AudioRouteMonitor, type IServiceAudioRoute } from './AudioRouteMonitor';
import type { IConfigDepViewPlatform, TViewTimer } from './platform/IConfigDepViewPlatform';
import { LiveKitRoomEventType } from '../../application/ports/IGatewayLiveKit';
import type {
  IConfigLiveKitConnect, IEntityLiveKitParticipant, IGatewayLiveKit, IPayloadLiveKitDisconnect, IServiceLiveKitAdapter, IStateLiveKitRoom,
  LiveKitConnectionState,
} from '../../application/ports/IGatewayLiveKit';
import type { IPortClientStateView, IEntitySessionView } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { RoomId, PlayerId } from '../../application/ports/ClientVocabulary';
import type { ClientRequests } from '../client/ClientRequests';

/** How often the idle rule is ticked while video is on at a table. */
const LK_IDLE_CHECK_INTERVAL_MS = 15 * 1000;
/** LiveKit's own reconnect gives up after a while with a terminal RoomDisconnected: the full connect is retried after this. */
const RECONNECT_DELAY_MS = 3_000;
const REMOVED_BY_SERVER: TClientUseCaseInput<'mediaRemovedByServer'> = {};

/** The session's state and its actions, as the screen reads them — the hook's shape but its two inputs (the binding adds them). */
export interface IStateLiveKitSession {
  readonly lkParticipants: ReadonlyMap<string, IEntityLiveKitParticipant>;
  readonly lkConnectionState: LiveKitConnectionState;
  readonly lkMicMuted: boolean;
  readonly lkCamMuted: boolean;
  readonly lkEnabled: boolean;
  readonly lkAutoDisconnected: boolean;
  readonly lkIdleBannerDismissed: boolean;
  readonly setLkIdleBannerDismissed: (v: boolean) => void;
  readonly toggleLkMic: () => Promise<void>;
  readonly toggleLkCam: () => Promise<void>;
  readonly setLkParticipantVolume: (participantId: string, volume: number) => void;
  readonly toggleLkConnection: () => void;
  readonly reconnectLk: () => void;
  readonly audioRoute: IServiceAudioRoute;
  readonly showEarphoneHint: boolean;
  readonly earphoneHintDismissed: boolean;
  readonly setEarphoneHintDismissed: (v: boolean) => void;
}

interface IStateLiveKitOwn {
  readonly participants: ReadonlyMap<string, IEntityLiveKitParticipant>;
  readonly connectionState: LiveKitConnectionState;
  readonly micMuted: boolean;
  readonly camMuted: boolean;
  readonly earphoneHintDismissed: boolean;
}

export interface IConfigDepLiveKitSession {
  readonly useCases: Pick<IPortClientUseCases,
    'toggleMedia' | 'reconnectMedia' | 'checkMediaIdle' | 'mediaRemovedByServer' | 'dismissMediaBanner' | 'mediaUsed' | 'mediaToken'
    | 'reportMediaConnection'>;
  readonly view: IPortClientStateView;
  readonly requests: ClientRequests;
  /** The host's LAZY client factory (`create()` downloads the SDK) — null where the host has no video. */
  readonly liveKit: Pick<IServiceLiveKitAdapter, 'create'> | null;
  readonly platform: Pick<IConfigDepViewPlatform, 'timers' | 'clock' | 'logger' | 'mediaDevices' | 'liveKitIdleTimeoutMs'>;
}

/** What a commit hands the session's reactions: the table and the player the screen is at, and the snapshot it rendered. */
export interface IPayloadLiveKitCommit {
  readonly roomId: RoomId | null;
  readonly playerId: PlayerId | null;
  readonly rendered: IStateLiveKitSession;
}

/** The state changes the session makes — named, as every argument bag to our own functions is (`lint:literals`). */
const TRACKS_MUTED: Partial<IStateLiveKitOwn> = { micMuted: true, camMuted: true };
const MIC_ON: Partial<IStateLiveKitOwn> = { micMuted: false };
const MIC_OFF: Partial<IStateLiveKitOwn> = { micMuted: true };
const CAM_ON: Partial<IStateLiveKitOwn> = { camMuted: false };
const CAM_OFF: Partial<IStateLiveKitOwn> = { camMuted: true };
const HINT_BACK: Partial<IStateLiveKitOwn> = { earphoneHintDismissed: false };
const CONNECTING: Partial<IStateLiveKitOwn> = { connectionState: 'connecting' };
const FAILED: Partial<IStateLiveKitOwn> = { connectionState: 'failed' };
const CONNECTED: Partial<IStateLiveKitOwn> = { connectionState: 'connected', camMuted: false, micMuted: false };
const SAME: Partial<IStateLiveKitOwn> = {};

const SILENT: IStateLiveKitOwn = {
  participants: new Map<string, IEntityLiveKitParticipant>(), connectionState: 'disconnected', micMuted: true, camMuted: true,
  earphoneHintDismissed: false,
};

/**
 * THE TABLE'S VIDEO CHAT, SDK SIDE (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/useLiveKitSession.ts` (strict-ports FE5c)
 * as a controller. The SDK's client, room, tracks and participants stay here — not JSON, main-thread only; the policy is the
 * Application's, read from the view's `media`, and the token and the connection reports go through use cases. The hook's seven
 * effects are seven reactions in the hook's order — the tracks muted on leaving a table, the idle tick, the audio route's three, the
 * earphone hint's reset, the session — so the order in which it calls the outside does not move (the corpus pins it); since P3
 * (§28.295) an eighth leads them, placing the actions at the commit's table, and AppRoot's model composes the list.
 *
 * **DEVIATION, flagged (§28.292's finding 2):** with video on and NO client factory on the host, the hook called `create()` on null
 * and the screen fell into its error boundary; the provider has always documented null as "no video", and now it is: no session.
 */
export class LiveKitSessionController implements IServiceViewModel<IStateLiveKitSession> {
  private readonly state = new ObservableState<IStateLiveKitOwn>(SILENT);
  private readonly audio: AudioRouteMonitor;
  private readonly declared: ReadonlyArray<IConfigReaction<IPayloadLiveKitCommit>>;
  private readonly reactions: Reactions<IPayloadLiveKitCommit>;
  /** The SDK client, kept across sessions once made: a table's next session reuses it. */
  private adapter: IGatewayLiveKit | null = null;
  /** The table and player of the last commit — what the actions act on. */
  private at: { readonly roomId: RoomId | null; readonly playerId: PlayerId | null } = { roomId: null, playerId: null };
  private shown: {
    readonly own: IStateLiveKitOwn; readonly media: IEntitySessionView['media']; readonly route: IServiceAudioRoute;
    readonly session: IStateLiveKitSession;
  } | null = null;

  constructor(private readonly deps: IConfigDepLiveKitSession) {
    this.audio = new AudioRouteMonitor(deps.platform.mediaDevices, deps.platform.timers);
    this.declared = [
      // Where the actions act: the table and player of the commit (the hook's callbacks closed over the render's).
      { deps: (facts) => [facts.roomId, facts.playerId], run: (facts) => { this.at = { roomId: facts.roomId, playerId: facts.playerId }; } },
      // Leaving the room mutes the SDK's tracks for the next one (the policy half resets in the view).
      { deps: (facts) => [facts.roomId], run: (facts) => { if (!facts.roomId) this.own(TRACKS_MUTED); } },
      // The idle rule (§23.7) is the Application's: this only ticks it, every 15 s while video is on at a table.
      ClientJobModel.reaction(deps.platform.timers,
        (facts: IPayloadLiveKitCommit) => [this.idleTick, LK_IDLE_CHECK_INTERVAL_MS, facts.rendered.lkEnabled && facts.roomId !== null] as const),
      ...this.audio.reactionsOver((facts: IPayloadLiveKitCommit) => facts.rendered.lkConnectionState === 'connected',
        (facts: IPayloadLiveKitCommit) => facts.rendered.audioRoute.notice),
      // A fresh disconnect must re-surface a previously dismissed banner.
      { deps: (facts) => [facts.rendered.audioRoute.notice],
        run: (facts) => { if (facts.rendered.audioRoute.notice === 'disconnected') this.own(HINT_BACK); } },
      { deps: (facts) => [facts.roomId, facts.playerId, facts.rendered.lkEnabled], run: (facts) => this.session(facts) },
    ];
    this.reactions = new Reactions<IPayloadLiveKitCommit>(this.declared);
  }

  /** The session's reactions over the facts of the screen's model that composes it (AppRoot's, P3, §28.295), in the hook's order. */
  reactionsOver<TFacts>(factsOf: (facts: TFacts) => IPayloadLiveKitCommit): Array<IConfigReaction<TFacts>> {
    return Reactions.over(this.declared, factsOf);
  }

  private own(change: Partial<IStateLiveKitOwn>): void {
    this.state.update(change);
  }


  // ── the actions ─────────────────────────────────────────────────────────────────────────────────────────────────────

  readonly setLkIdleBannerDismissed = (dismissed: boolean): void => {
    const command: TClientUseCaseInput<'dismissMediaBanner'> = { dismissed };
    void this.deps.useCases.dismissMediaBanner(this.deps.requests.context(), command);
  };

  readonly toggleLkMic = async (): Promise<void> => {
    const adapter = this.adapter;
    const roomId = this.at.roomId;
    if (!adapter || !roomId) return;
    if (this.state.get().micMuted) {
      await adapter.enableMicrophone(roomId);
      this.own(MIC_ON);
      // Reported once per room, never for a guest — the Application's rule (`planPresence`).
      const used: TClientUseCaseInput<'mediaUsed'> = { roomId, kind: 'audio' };
      void this.deps.useCases.mediaUsed(this.deps.requests.context(), used);
    } else {
      await adapter.disableMicrophone(roomId);
      this.own(MIC_OFF);
    }
  };

  readonly toggleLkCam = async (): Promise<void> => {
    const adapter = this.adapter;
    const roomId = this.at.roomId;
    if (!adapter || !roomId) return;
    if (this.state.get().camMuted) {
      await adapter.enableCamera(roomId);
      this.own(CAM_ON);
      const used: TClientUseCaseInput<'mediaUsed'> = { roomId, kind: 'video' };
      void this.deps.useCases.mediaUsed(this.deps.requests.context(), used);
    } else {
      await adapter.disableCamera(roomId);
      this.own(CAM_OFF);
    }
  };

  readonly setLkParticipantVolume = (participantId: string, volume: number): void => {
    const adapter = this.adapter;
    const roomId = this.at.roomId;
    if (!adapter || !roomId) return;
    adapter.setParticipantVolume(roomId, participantId, volume);
  };

  readonly toggleLkConnection = (): void => {
    const command: TClientUseCaseInput<'toggleMedia'> = { now: this.deps.platform.clock.now() };
    void this.deps.useCases.toggleMedia(this.deps.requests.context(), command);
  };

  readonly reconnectLk = (): void => {
    const command: TClientUseCaseInput<'reconnectMedia'> = { now: this.deps.platform.clock.now() };
    void this.deps.useCases.reconnectMedia(this.deps.requests.context(), command);
  };

  readonly setEarphoneHintDismissed = (dismissed: boolean): void => {
    const hint: Partial<IStateLiveKitOwn> = { earphoneHintDismissed: dismissed };
    this.own(hint);
  };

  /** One tick of the idle rule, with the browser's clock and the configured timeout. */
  private readonly idleTick = (): void => {
    const command: TClientUseCaseInput<'checkMediaIdle'> = {
      now: this.deps.platform.clock.now(), idleTimeoutMs: this.deps.platform.liveKitIdleTimeoutMs,
    };
    void this.deps.useCases.checkMediaIdle(this.deps.requests.context(), command);
  };

  // ── the session ─────────────────────────────────────────────────────────────────────────────────────────────────────

  /** The participants as the room reports them now — or null when the room cannot be read. */
  private participantsOf(adapter: IGatewayLiveKit, roomId: RoomId): ReadonlyMap<string, IEntityLiveKitParticipant> | null {
    const r = adapter.getRoomState(roomId);
    if (!r.success) return null;
    const next = new Map<string, IEntityLiveKitParticipant>();
    for (const t of r.value.participantTracks) {
      next.set(t.participantIdentity, {
        participantId: t.participantSid,
        playerId: t.participantIdentity as PlayerId,
        displayName: t.participantIdentity,
        isCameraEnabled: t.isCameraEnabled,
        isMicrophoneEnabled: t.isMicEnabled,
        videoTrack: t.videoTrack,
        audioTrack: t.audioTrack,
      });
    }
    return next;
  }

  /** The state change and, when the room can be read, its participants — one change, as the hook's batched updates were one render. */
  private ownWithParticipants(adapter: IGatewayLiveKit, roomId: RoomId, change: Partial<IStateLiveKitOwn>): void {
    const participants = this.participantsOf(adapter, roomId);
    this.own(participants === null ? change : { ...change, participants });
  }

  private session(facts: IPayloadLiveKitCommit): (() => void) | undefined {
    const { roomId, playerId } = facts;
    const factory = this.deps.liveKit;
    if (!roomId || !playerId || !facts.rendered.lkEnabled || factory === null) return undefined;

    let alive = true;
    // The room and player THIS connection is for — reported as they were, even after the table moves on.
    const connected: TClientUseCaseInput<'reportMediaConnection'> = { roomId, playerId, connected: true };
    const disconnected: TClientUseCaseInput<'reportMediaConnection'> = { ...connected, connected: false };
    let cleanup: (() => void) | null = null;
    let retryTimer: TViewTimer | null = null;

    // livekit-client is loaded lazily, by the factory's create(): it never runs at app startup.
    factory.create().then(
      (resolvedAdapter) => {
        if (!alive) return;
        if (!this.adapter) this.adapter = resolvedAdapter;
        const adapter = this.adapter;

        const connect = async (): Promise<void> => {
          // Defence-in-depth only — unreachable today, and deliberately kept: connect() has two callers and `alive` is true at
          // both (the initial call, in the same synchronous run as the guard above; the retry, whose timer a teardown clears).
          // It becomes load-bearing the moment anyone adds an await before either call site (handbook §28.3).
          /* c8 ignore next */
          if (!alive) return;

          this.own(CONNECTING);
          const tokenRequest: TClientUseCaseInput<'mediaToken'> = { roomId, playerId };
          const tokenResult = await this.deps.useCases.mediaToken(this.deps.requests.context(), tokenRequest);
          if (!alive) return;
          if (!tokenResult.success) {
            this.own(FAILED);
            return;
          }
          const { token, serverUrl } = tokenResult.value;
          const liveKitConnectOptions: IConfigLiveKitConnect = {
            roomId, playerId, liveKitToken: token, serverUrl, enableVideo: true, enableAudio: true,
          };
          const result = await adapter.connect(liveKitConnectOptions);
          if (!alive) return;
          if (result.success) {
            this.ownWithParticipants(adapter, roomId, CONNECTED);
            void this.deps.useCases.reportMediaConnection(this.deps.requests.context(), connected);
          } else {
            this.deps.platform.logger.warn('[LiveKit] connect failed:', result.error.message);
            this.own(FAILED);
          }
        };

        const unsubscribe = adapter.onRoomEvent((ev: { type: string; payload: unknown }) => {
          switch (ev.type) {
            case LiveKitRoomEventType.ConnectionStateChanged:
              // Already the Application's vocabulary: the adapter maps the SDK's state (FE5d).
              {
                const reported: Partial<IStateLiveKitOwn> = { connectionState: (ev.payload as IStateLiveKitRoom).connectionState };
                this.ownWithParticipants(adapter, roomId, reported);
              }
              break;
            case LiveKitRoomEventType.TrackSubscribed:
            case LiveKitRoomEventType.TrackUnsubscribed:
            case LiveKitRoomEventType.LocalTrackPublished:
            case LiveKitRoomEventType.LocalTrackUnpublished:
            case LiveKitRoomEventType.ParticipantConnected:
            case LiveKitRoomEventType.ParticipantDisconnected:
              this.ownWithParticipants(adapter, roomId, SAME);
              break;
            case LiveKitRoomEventType.RoomDisconnected:
              // The backend's idle sweep removed us ON PURPOSE (§21.10: the whole table was idle). Rejoining by itself would undo
              // that, so do what the local idle rule does: video off, banner up, one tap to reconnect. Turning video off ends this
              // session, whose stop reports the disconnect.
              if ((ev.payload as IPayloadLiveKitDisconnect | null)?.removedByServer === true) {
                void this.deps.useCases.mediaRemovedByServer(this.deps.requests.context(), REMOVED_BY_SERVER);
                break;
              }
              {
                const dropped: Partial<IStateLiveKitOwn> = { connectionState: 'disconnected', participants: new Map<string, IEntityLiveKitParticipant>() };
                this.own(dropped);
              }
              // Close out the session that just ended — otherwise it stays open (durationMs: null) and drops out of the usage
              // reports, though the reconnect below opens a new one.
              void this.deps.useCases.reportMediaConnection(this.deps.requests.context(), disconnected);
              // LiveKit's own reconnect attempts are exhausted — retry the full connect (fresh token) after a short delay.
              retryTimer = this.deps.platform.timers.setTimeout(() => {
                retryTimer = null;
                connect().catch(() => {});
              }, RECONNECT_DELAY_MS);
              break;
          }
        });

        cleanup = () => {
          unsubscribe();
          if (retryTimer) {
            this.deps.platform.timers.clearTimeout(retryTimer);
            retryTimer = null;
          }
          adapter.disconnect(roomId).catch(() => {});
          void this.deps.useCases.reportMediaConnection(this.deps.requests.context(), disconnected);
        };

        void connect();
      },
    ).catch((err) => {
      if (alive) {
        this.deps.platform.logger.warn('[LiveKit] init failed:', err);
        this.own(FAILED);
      }
    });

    return () => {
      alive = false;
      cleanup?.();
      const ended: Partial<IStateLiveKitOwn> = {
        connectionState: 'disconnected', participants: new Map<string, IEntityLiveKitParticipant>(), camMuted: true, micMuted: true,
      };
      this.own(ended);
    };
  }

  // ── the view-model surface ──────────────────────────────────────────────────────────────────────────────────────────

  /** The session as the screen reads it — the same object until its own state, the media policy or the audio route changes. */
  getSnapshot(): IStateLiveKitSession {
    const own = this.state.get();
    const media = this.deps.view.getSnapshot().session.media;
    const route = this.audio.getSnapshot();
    if (this.shown !== null && this.shown.own === own && this.shown.media === media && this.shown.route === route) return this.shown.session;
    const session: IStateLiveKitSession = {
      lkParticipants: own.participants,
      lkConnectionState: own.connectionState,
      lkMicMuted: own.micMuted,
      lkCamMuted: own.camMuted,
      // The policy, as the Application keeps it for the current table (a state kept for another room reads as fresh).
      lkEnabled: media.enabled,
      lkAutoDisconnected: media.autoDisconnected,
      lkIdleBannerDismissed: media.idleBannerDismissed,
      setLkIdleBannerDismissed: this.setLkIdleBannerDismissed,
      toggleLkMic: this.toggleLkMic,
      toggleLkCam: this.toggleLkCam,
      setLkParticipantVolume: this.setLkParticipantVolume,
      toggleLkConnection: this.toggleLkConnection,
      reconnectLk: this.reconnectLk,
      audioRoute: route,
      showEarphoneHint: own.connectionState === 'connected' && route.route !== 'headphones',
      earphoneHintDismissed: own.earphoneHintDismissed,
      setEarphoneHintDismissed: this.setEarphoneHintDismissed,
    };
    this.shown = { own, media, route, session };
    return session;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveAudio = this.audio.subscribe(listener);
    const leaveView = this.deps.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveAudio();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[], rendered: IStateLiveKitSession): void {
    this.reactions.stopChanged(LiveKitSessionController.factsOf(inputs, rendered));
  }

  commit(inputs: readonly unknown[], rendered: IStateLiveKitSession): void {
    this.reactions.startChanged(LiveKitSessionController.factsOf(inputs, rendered));
  }

  private static factsOf(inputs: readonly unknown[], rendered: IStateLiveKitSession): IPayloadLiveKitCommit {
    const [roomId, playerId] = inputs as readonly [RoomId | null, PlayerId | null];
    return { roomId: roomId ?? null, playerId: playerId ?? null, rendered };
  }
}
